export const COLLECTIONS = ['tasks', 'events', 'custom-occasions'] as const
export type CollectionName = typeof COLLECTIONS[number]

export type ImportSource = Partial<Record<CollectionName, unknown>>

// One past the highest seq already in the collection, so a new row always
// lands after the ones the user has already built up.
export function nextSeq(rows: { seq?: number }[]): number {
    let max = 0
    for (const row of rows) {
        const n = row.seq ?? 0
        if (n > max) max = n
    }
    return max + 1
}

// Array.sort is stable, so rows that share a seq (legacy rows without one)
// keep the order the engine loaded them in.
export function sortBySeq<T extends { seq?: number }>(rows: T[]): T[] {
    return rows.slice().sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0))
}

// 1.6.0 stored a whole array under each key. Drop anything that is not a
// row with a string id, and stamp insertion order so IndexedDB — which
// returns key order — can put them back the way the user added them.
export type ImportedRow = { id: string; seq: number } & Record<string, unknown>

export function rowsFrom(stored: unknown): ImportedRow[] {
    if (!Array.isArray(stored)) return []
    const rows: ImportedRow[] = []
    for (const row of stored) {
        if (!row || typeof row !== 'object') continue
        const id = (row as { id?: unknown }).id
        if (typeof id !== 'string') continue
        rows.push({ ...(row as object), id, seq: rows.length + 1 })
    }
    return rows
}

export function prepareImport(source: ImportSource): Record<CollectionName, ImportedRow[]> {
    return {
        tasks: rowsFrom(source.tasks),
        events: rowsFrom(source.events),
        'custom-occasions': rowsFrom(source['custom-occasions']),
    }
}
